// Public profile data + verified mutual-reference stats. Reads via the
// server publishable client (relies on existing public SELECT policies on
// profiles and ratings).
import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { z } from "zod";
import type { Database } from "@/integrations/supabase/types";

function publicClient() {
  return createClient<Database>(
    process.env.SUPABASE_URL!,
    process.env.SUPABASE_PUBLISHABLE_KEY!,
    { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
  );
}

export type PublicProfile = {
  id: string;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
  bio: string | null;
  country: string | null;
  created_at: string;
  buyer_rating_avg: number;
  buyer_rating_count: number;
  seller_rating_avg: number;
  seller_rating_count: number;
  seller_completed_count: number;
  buyer_strike_count: number;
  trusted_seller: boolean;
  verified_business: boolean;
  verified_store: boolean;
  tcg_specialisations: string[];
};

export type VerifiedReferenceRow = {
  id: string;
  score: number;
  comment: string | null;
  created_at: string;
  mutual_verified: boolean;
  rater_username: string | null;
};

export type PublicProfileData = {
  profile: PublicProfile | null;
  verifiedAsBuyer: number;
  verifiedAsSeller: number;
  recent: VerifiedReferenceRow[];
};

export const getPublicProfileByUsername = createServerFn({ method: "GET" })
  .inputValidator((i: unknown) =>
    z.object({ username: z.string().trim().toLowerCase().min(1).max(40) }).parse(i),
  )
  .handler(async ({ data }): Promise<PublicProfileData> => {
    const supabase = publicClient();
    const { data: profile } = await supabase
      .from("profiles")
      .select(
        "id, username, display_name, avatar_url, bio, country, created_at, buyer_rating_avg, buyer_rating_count, seller_rating_avg, seller_rating_count, seller_completed_count, buyer_strike_count, trusted_seller, verified_business, verified_store, tcg_specialisations",
      )
      .eq("username", data.username)
      .maybeSingle();

    if (!profile) {
      return { profile: null, verifiedAsBuyer: 0, verifiedAsSeller: 0, recent: [] };
    }

    // Verified references split by ratee role (buyer vs seller in the txn).
    const [{ data: txnsAsBuyer }, { data: txnsAsSeller }] = await Promise.all([
      supabase.from("transactions").select("id").eq("buyer_id", profile.id),
      supabase.from("transactions").select("id").eq("seller_id", profile.id),
    ]);
    const buyerTxnIds = (txnsAsBuyer ?? []).map((t) => t.id);
    const sellerTxnIds = (txnsAsSeller ?? []).map((t) => t.id);

    const [{ count: verifiedAsBuyer }, { count: verifiedAsSeller }] = await Promise.all([
      buyerTxnIds.length
        ? supabase
            .from("ratings")
            .select("id", { count: "exact", head: true })
            .eq("mutual_verified", true)
            .eq("ratee_id", profile.id)
            .in("transaction_id", buyerTxnIds)
        : Promise.resolve({ count: 0 } as { count: number }),
      sellerTxnIds.length
        ? supabase
            .from("ratings")
            .select("id", { count: "exact", head: true })
            .eq("mutual_verified", true)
            .eq("ratee_id", profile.id)
            .in("transaction_id", sellerTxnIds)
        : Promise.resolve({ count: 0 } as { count: number }),
    ]);

    const { data: recentRows } = await supabase
      .from("ratings")
      .select("id, score, comment, created_at, mutual_verified, rater_id")
      .eq("ratee_id", profile.id)
      .eq("mutual_verified", true)
      .order("created_at", { ascending: false })
      .limit(10);

    const raterIds = Array.from(new Set((recentRows ?? []).map((r) => r.rater_id)));
    const raterMap = new Map<string, string>();
    if (raterIds.length) {
      const { data: raters } = await supabase
        .from("profiles")
        .select("id, username")
        .in("id", raterIds);
      for (const r of raters ?? []) raterMap.set(r.id, r.username);
    }

    const recent: VerifiedReferenceRow[] = (recentRows ?? []).map((r) => ({
      id: r.id,
      score: r.score,
      comment: r.comment,
      created_at: r.created_at,
      mutual_verified: r.mutual_verified,
      rater_username: raterMap.get(r.rater_id) ?? null,
    }));

    return {
      profile: profile as PublicProfile,
      verifiedAsBuyer: verifiedAsBuyer ?? 0,
      verifiedAsSeller: verifiedAsSeller ?? 0,
      recent,
    };
  });
